import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Users, Search, RefreshCw, Eye, Pencil, KeyRound, Baby,
  Building2, Phone, User, Shield, EyeOff, Loader2
} from "lucide-react";
import { useState, useMemo } from "react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ar } from "date-fns/locale";

const roleLabels: Record<string, string> = {
  admin: "مدير حضانة", teacher: "معلم", parent: "ولي أمر",
  super_admin: "مشرف عام", support: "دعم فني", accountant: "محاسب",
  developer: "مطوّر", call_center: "كول سنتر", supervisor: "مشرف",
};
const roleColors: Record<string, string> = {
  admin: "bg-blue-100 text-blue-800", teacher: "bg-green-100 text-green-800",
  parent: "bg-purple-100 text-purple-800", super_admin: "bg-amber-100 text-amber-800",
  support: "bg-teal-100 text-teal-800", accountant: "bg-orange-100 text-orange-800",
};

const SupportUsers = () => {
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();

  const { data: users, isLoading, refetch } = useQuery({
    queryKey: ["support-users"],
    queryFn: async () => {
      const { data: profiles, error } = await supabase
        .from("profiles")
        .select("*, nurseries(name)")
        .order("created_at", { ascending: false });
      if (error) throw error;

      const { data: roles } = await supabase.from("user_roles").select("*");
      const rolesMap = new Map<string, string[]>();
      roles?.forEach((r) => {
        const existing = rolesMap.get(r.user_id) || [];
        existing.push(r.role);
        rolesMap.set(r.user_id, existing);
      });

      return profiles?.map((p) => ({
        ...p,
        roles: rolesMap.get(p.user_id) || [],
      })) || [];
    },
  });

  const { data: children } = useQuery({
    queryKey: ["support-children"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("children")
        .select("id, full_name, parent_id, nursery_id, class_id, nurseries(name), classes(name)")
        .order("full_name");
      if (error) throw error;
      return data || [];
    },
  });

  const filtered = useMemo(() => {
    if (!users) return [];
    return users.filter((u: any) => {
      const matchSearch = !search ||
        u.full_name?.toLowerCase().includes(search.toLowerCase()) ||
        u.phone?.includes(search) ||
        u.user_id?.includes(search);
      const matchRole = roleFilter === "all" || u.roles.includes(roleFilter);
      return matchSearch && matchRole;
    });
  }, [users, search, roleFilter]);

  const userChildren = useMemo(() => {
    if (!selectedUser || !children) return [];
    return children.filter((c: any) => c.parent_id === selectedUser.user_id);
  }, [selectedUser, children]);

  const openDetails = (user: any) => {
    setSelectedUser(user);
    setDetailsOpen(true);
  };

  const openEdit = (user: any) => {
    setSelectedUser(user);
    setEditName(user.full_name || "");
    setEditPhone(user.phone || "");
    setEditOpen(true);
  };

  const openResetPw = (user: any) => {
    setSelectedUser(user);
    setNewPassword("");
    setShowPw(false);
    setResetPwOpen(true);
  };

  const handleEditSave = async () => {
    if (!editName.trim()) { toast.error("الاسم مطلوب"); return; }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("support-manage-user", {
        body: {
          action: "update_profile",
          target_user_id: selectedUser.user_id,
          full_name: editName.trim(),
          phone: editPhone.trim(),
        },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      toast.success("تم تحديث البيانات بنجاح ✅");
      setEditOpen(false);
      queryClient.invalidateQueries({ queryKey: ["support-users"] });
    } catch (err: any) {
      toast.error(err.message || "فشل التحديث");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error("كلمة المرور يجب أن تكون 6 أحرف على الأقل");
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("support-manage-user", {
        body: {
          action: "reset_password",
          target_user_id: selectedUser.user_id,
          new_password: newPassword,
        },
      });
      if (error || data?.error) throw new Error(data?.error || error?.message);
      toast.success("تم إعادة تعيين كلمة المرور بنجاح ✅");
      setResetPwOpen(false);
    } catch (err: any) {
      toast.error(err.message || "فشلت العملية");
    } finally {
      setLoading(false);
    }
  };

  const stats = useMemo(() => {
    if (!users) return { total: 0, parents: 0, admins: 0, teachers: 0 };
    return {
      total: users.length,
      parents: users.filter((u: any) => u.roles.includes("parent")).length,
      admins: users.filter((u: any) => u.roles.includes("admin")).length,
      teachers: users.filter((u: any) => u.roles.includes("teacher")).length,
    };
  }, [users]);

  return (
    <div className="space-y-6" dir="rtl">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Users className="w-6 h-6 text-teal-600" />
          إدارة المستخدمين
        </h1>
        <p className="text-muted-foreground text-sm mt-1">البحث عن المستخدمين وحل مشاكلهم — تعديل البيانات، إعادة تعيين كلمة المرور</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "إجمالي", value: stats.total, icon: Users, color: "text-primary" },
          { label: "أولياء أمور", value: stats.parents, icon: Baby, color: "text-purple-500" },
          { label: "مدراء حضانات", value: stats.admins, icon: Building2, color: "text-blue-500" },
          { label: "معلمون", value: stats.teachers, icon: User, color: "text-green-500" },
        ].map((s) => (
          <Card key={s.label}>
            <CardContent className="p-4 flex items-center gap-3">
              <s.icon className={`w-5 h-5 ${s.color}`} />
              <div>
                <p className="text-2xl font-bold">{s.value}</p>
                <p className="text-xs text-muted-foreground">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Users Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
            <CardTitle className="text-base">المستخدمون ({filtered.length})</CardTitle>
            <div className="flex-1" />
            <div className="flex flex-wrap gap-2 w-full md:w-auto">
              <div className="relative flex-1 md:flex-none md:w-60">
                <Search className="absolute right-3 top-2.5 w-4 h-4 text-muted-foreground" />
                <Input placeholder="بحث بالاسم أو الهاتف أو ID..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-9" />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="الدور" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الأدوار</SelectItem>
                  <SelectItem value="parent">ولي أمر</SelectItem>
                  <SelectItem value="admin">مدير حضانة</SelectItem>
                  <SelectItem value="teacher">معلم</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="icon" onClick={() => refetch()}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
              <span className="mr-2 text-muted-foreground">جاري التحميل...</span>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>المستخدم</TableHead>
                  <TableHead>الهاتف</TableHead>
                  <TableHead>الحضانة</TableHead>
                  <TableHead>الأدوار</TableHead>
                  <TableHead>التسجيل</TableHead>
                  <TableHead className="w-32">إجراءات</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((user: any) => (
                  <TableRow key={user.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs shrink-0">
                          {user.full_name?.charAt(0) || "؟"}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{user.full_name || "بدون اسم"}</p>
                          <p className="text-[10px] text-muted-foreground">{user.user_id?.slice(0, 8)}...</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell dir="ltr" className="text-sm">{user.phone || "—"}</TableCell>
                    <TableCell className="text-sm">{(user as any).nurseries?.name || "—"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {user.roles.map((r: string) => (
                          <span key={r} className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${roleColors[r] || "bg-muted"}`}>
                            {roleLabels[r] || r}
                          </span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs">{format(new Date(user.created_at), "dd MMM yyyy", { locale: ar })}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="تفاصيل" onClick={() => openDetails(user)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" title="تعديل" onClick={() => openEdit(user)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600" title="إعادة كلمة المرور" onClick={() => openResetPw(user)}>
                          <KeyRound className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!filtered.length && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                      لا يوجد مستخدمون مطابقون
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent dir="rtl" className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-primary" />
              تفاصيل المستخدم
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-lg">
                  {selectedUser.full_name?.charAt(0) || "؟"}
                </div>
                <div>
                  <p className="font-semibold">{selectedUser.full_name || "بدون اسم"}</p>
                  <p className="text-xs text-muted-foreground">ID: {selectedUser.user_id?.slice(0, 12)}...</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs">الهاتف</p>
                  <p className="font-medium" dir="ltr">{selectedUser.phone || "—"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs">الحضانة</p>
                  <p className="font-medium">{(selectedUser as any).nurseries?.name || "بدون حضانة"}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs">تاريخ التسجيل</p>
                  <p className="font-medium">{format(new Date(selectedUser.created_at), "dd MMMM yyyy", { locale: ar })}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-muted-foreground text-xs">الأدوار</p>
                  <div className="flex gap-1 flex-wrap">
                    {selectedUser.roles?.map((r: string) => (
                      <span key={r} className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${roleColors[r] || "bg-muted"}`}>
                        {roleLabels[r] || r}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Children */}
              {userChildren.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2 flex items-center gap-1">
                    <Baby className="w-4 h-4" />
                    الأطفال المرتبطون ({userChildren.length})
                  </p>
                  <div className="space-y-2">
                    {userChildren.map((c: any) => (
                      <div key={c.id} className="flex items-center justify-between p-2 rounded border text-sm">
                        <span className="font-medium">{c.full_name}</span>
                        <div className="flex gap-2 text-xs text-muted-foreground">
                          <span>{(c as any).nurseries?.name || "—"}</span>
                          <span>•</span>
                          <span>{(c as any).classes?.name || "بدون فصل"}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1 gap-1" onClick={() => { setDetailsOpen(false); openEdit(selectedUser); }}>
                  <Pencil className="w-4 h-4" />
                  تعديل البيانات
                </Button>
                <Button variant="outline" className="flex-1 gap-1 text-amber-600 border-amber-200 hover:bg-amber-50"
                  onClick={() => { setDetailsOpen(false); openResetPw(selectedUser); }}>
                  <KeyRound className="w-4 h-4" />
                  إعادة كلمة المرور
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent dir="rtl" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-primary" />
              تعديل بيانات المستخدم
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              تعديل بيانات: <span className="font-bold text-foreground">{selectedUser?.full_name}</span>
            </p>
            <div className="space-y-2">
              <Label>الاسم الكامل</Label>
              <Input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="الاسم" />
            </div>
            <div className="space-y-2">
              <Label>رقم الهاتف</Label>
              <Input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} placeholder="05xxxxxxxx" dir="ltr" />
            </div>
            <Button className="w-full" onClick={handleEditSave} disabled={loading}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
              حفظ التعديلات
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPwOpen} onOpenChange={setResetPwOpen}>
        <DialogContent dir="rtl" className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <KeyRound className="w-5 h-5" />
              إعادة تعيين كلمة المرور
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              إعادة تعيين كلمة مرور <span className="font-bold text-foreground">{selectedUser?.full_name}</span>
            </p>
            <div>
              <Label>كلمة المرور الجديدة *</Label>
              <div className="relative">
                <Input
                  type={showPw ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="6 أحرف على الأقل"
                  dir="ltr"
                  minLength={6}
                />
                <button type="button" className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPw(!showPw)}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button className="w-full" variant="default" onClick={handleResetPassword} disabled={loading || newPassword.length < 6}>
              {loading ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : null}
              تأكيد إعادة التعيين
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SupportUsers;
